import { useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import PlansContext from 'src/contexts/PlansContext';
import { RespectType, WeekDayEn } from 'src/entities/planningEntity';
import { RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';

import { planningInitialState, planningReducer } from '../reducers';

type UsePlanningSettingsParams = {
  activateConstraintLoadAfterNotCleared: boolean;
  loadAfterInitialStock: boolean;
};

export const usePlanningSettings = ({
  activateConstraintLoadAfterNotCleared,
  loadAfterInitialStock,
}: UsePlanningSettingsParams) => {
  const plansContext = useContext(PlansContext);

  const [planning, dispatchPlanning] = useReducer(planningReducer, planningInitialState);

  useEffect(() => {
    plansContext.setPlanningEntity(planning);
  }, [planning]);

  const emptyArray = useMemo(() => [], []);
  const selectedRelaxedRules = useMemo(() => (
    planning.selectedRelaxedRules || emptyArray as RelaxedRuleVo[]
  ), [emptyArray, planning.selectedRelaxedRules]);

  const setSelectedRelaxedRules = useCallback((rules: RelaxedRuleVo[]) => {
    dispatchPlanning({
      type: 'updateSelectedRelaxedRules',
      payload: rules
    });
  }, []);

  const setPriorityLargeTrucks = useCallback((priority: number) => {
    dispatchPlanning({
      type: 'updatePriorityLargeTrucks',
      payload: priority
    });
  }, []);

  const updateBalancedLoading = useCallback((balancedLoading: boolean) => {
    dispatchPlanning({
      type: 'updateBalancedLoading',
      payload: balancedLoading
    });
  }, []);

  const updateIsRespectRecent = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateIsRespectRecent',
      payload: bool
    });
  }, []);

  const updateExcludeToll = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateExcludeToll',
      payload: bool
    });
  }, []);

  const updateRespectType = useCallback((respectType: RespectType) => {
    dispatchPlanning({
      type: 'updateRespectType',
      payload: respectType
    });
  }, []);

  const updateRespectDaysOfWeek = useCallback((days: WeekDayEn[]) => {
    dispatchPlanning({
      type: 'updateRespectDaysOfWeek',
      payload: days
    });
  }, []);

  const updateRespectOn = useCallback((payload: string) => {
    dispatchPlanning({
      type: 'updateRespectOn',
      payload
    });
  }, []);

  const updateIsTimeTableBackward = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateIsTimeTableBackward',
      payload: bool
    });
  }, []);

  const updateIncludeFutureDeliveries = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateIncludeFutureDeliveries',
      payload: bool
    });
  }, []);

  const updateAutoSplitOrders = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateAutoSplitOrders',
      payload: bool
    });
  }, []);

  const updateDontExchangePreroute = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateDontExchangePreroute',
      payload: bool,
    });
  }, []);

  const updateConcurrentAllOrNothing = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateConcurrentAllOrNothing',
      payload: bool,
    });
  }, []);

  const updateMLPlanning = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateMLPlanning',
      payload: bool,
    });
  }, []);

  const updateExpandWorkingStartMinutes = useCallback((minutes: number) => {
    dispatchPlanning({
      type: 'updateExpandWorkingStartTime',
      payload: minutes * 60
    });
  }, []);

  const updateExpandWorkingEndMinutes = useCallback((minutes: number) => {
    dispatchPlanning({
      type: 'updateExpandWorkingEndTime',
      payload: minutes * 60
    });
  }, []);

  const updateExpandLoadStartMinutes = useCallback((minutes: number) => {
    dispatchPlanning({
      type: 'updateExpandLoadStartTime',
      payload: minutes * 60
    });
  }, []);

  const updateExpandLoadEndMinutes = useCallback((minutes: number) => {
    dispatchPlanning({
      type: 'updateExpandLoadEndTime',
      payload: minutes * 60
    });
  }, []);

  const updateExpandUnloadStartMinutes = useCallback((minutes: number) => {
    dispatchPlanning({
      type: 'updateExpandUnloadStartTime',
      payload: minutes * 60
    });
  }, []);

  const updateExpandUnloadEndMinutes = useCallback((minutes: number) => {
    dispatchPlanning({
      type: 'updateExpandUnloadEndTime',
      payload: minutes * 60
    });
  }, []);

  const updateExpandMaxVolumeRate = useCallback((rate: number) => {
    dispatchPlanning({
      type: 'updateExpandMaxVolumeRate',
      payload: rate / 100.0
    });
  }, []);

  const updateExpandMaxLoadCapacityRate = useCallback((rate: number) => {
    dispatchPlanning({
      type: 'updateExpandMaxLoadCapacityRate',
      payload: rate / 100.0
    });
  }, []);

  const updateMLSourceType = useCallback((type: number) => {
    dispatchPlanning({
      type: 'updateMLSourceType',
      payload: type,
    });
  }, []);

  const updateOptimizeForLoad = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateOptimizeForLoad',
      payload: bool,
    });
  }, []);

  const updateForceNullBase = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateForceNullBase',
      payload: bool,
    });
  }, []);

  const updateTrunkTransportation = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateTrunkTransportation',
      payload: bool,
    });
  }, []);

  const updateActivateIncrementalMaxNumPistons = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateActivateIncrementalMaxNumPistons',
      payload: bool,
    });
  }, []);

  const updateTraceMode = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateTraceMode',
      payload: bool,
    });
  }, []);

  useEffect(() => {
    dispatchPlanning({
      type: 'updateActivateConstraintLoadAfterNotCleared',
      payload: activateConstraintLoadAfterNotCleared,
    });
  }, [activateConstraintLoadAfterNotCleared]);

  useEffect(() => {
    dispatchPlanning({
      type: 'updateLoadAfterInitialStock',
      payload: loadAfterInitialStock,
    });
  }, [loadAfterInitialStock]);

  return {
    planning,
    dispatchPlanning,
    selectedRelaxedRules,
    setSelectedRelaxedRules,
    setPriorityLargeTrucks,
    updateBalancedLoading,
    updateIsRespectRecent,
    updateExcludeToll,
    updateRespectType,
    updateRespectDaysOfWeek,
    updateRespectOn,
    updateIsTimeTableBackward,
    updateIncludeFutureDeliveries,
    updateAutoSplitOrders,
    updateDontExchangePreroute,
    updateConcurrentAllOrNothing,
    updateMLPlanning,
    updateExpandWorkingStartMinutes,
    updateExpandWorkingEndMinutes,
    updateExpandLoadStartMinutes,
    updateExpandLoadEndMinutes,
    updateExpandUnloadStartMinutes,
    updateExpandUnloadEndMinutes,
    updateExpandMaxVolumeRate,
    updateExpandMaxLoadCapacityRate,
    updateMLSourceType,
    updateOptimizeForLoad,
    updateForceNullBase,
    updateTrunkTransportation,
    updateActivateIncrementalMaxNumPistons,
    updateTraceMode,
  };
};
